import { SecurityPanel } from './SecurityPanel';
import '@/styles/settings-layout.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import type { Locale } from '@/lib/i18n';
import {
  useSettings,
  useUpdateNotification,
  useUpdateSettingsGeneral,
  type EventNotificationRow,
} from '@/hooks/useSettings';

type TabId = 'general-tab' | 'notifications-tab' | 'restrictions-tab';

function SearchableSelect({
  id,
  value,
  options,
  onChange,
  className,
  placeholder,
}: {
  id: string;
  value: string;
  options: Record<string, string>;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
}) {
  const [filter, setFilter] = useState('');
  const entries = useMemo(() => Object.entries(options), [options]);
  const filtered = useMemo(() => {
    if (!filter.trim()) return entries;
    const q = filter.toLowerCase();
    return entries.filter(([k, label]) => k.toLowerCase().includes(q) || label.toLowerCase().includes(q));
  }, [entries, filter]);

  return (
    <div className="settings-searchable-select">
      <input
        type="text"
        aria-label={id + " search"}
        className="settings-input-text browser-default"
        placeholder={placeholder}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ marginBottom: 6, width: '100%' }}
      />
      <select
        id={id}
        className={className}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size={filter ? Math.min(8, Math.max(3, filtered.length)) : undefined}
        style={filter ? { width: '100%' } : undefined}
      >
        {filtered.map(([key, label]) => (
          <option key={key || '__empty'} value={key}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
}

function GeneralTab() {
  const { t, setLocale, locale } = useLocale();
  const { data, isLoading } = useSettings();
  const update = useUpdateSettingsGeneral();
  const [language, setLanguage] = useState<string>(locale);
  const [timezone, setTimezone] = useState('');
  const [country, setCountry] = useState('');
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (!data) return;
    setLanguage(data.accountSettings.language || locale);
    setTimezone(data.accountSettings.timezone || '');
    setCountry(data.accountSettings.country || '');
  }, [data, locale]);
  if (isLoading || !data) return <p role="status">{t('messages_loading')}</p>;
  return <form className="settings-general-panel" onSubmit={async event => {
    event.preventDefault(); setMessage('');
    try {
      const result = await update.mutateAsync({ language, timezone, country });
      if (result.success) setLocale(language as Locale);
      setMessage(result.message || t('security_request_error'));
    } catch { setMessage(t('security_request_error')); }
  }}>
    <h2>{t('settings_tab_general')}</h2><p>{t('security_general_help')}</p>
    <div className="settings-general-grid">
      <label htmlFor="settings-language">{t('settings_label_language')}<select id="settings-language" value={language} onChange={event => setLanguage(event.target.value)}>{Object.entries(data.languages).map(([key, name]) => <option key={key} value={key}>{name}</option>)}</select></label>
      <div><label htmlFor="settings-country">{t('settings_label_country')}</label><SearchableSelect id="settings-country" value={country} options={data.countries} onChange={setCountry} placeholder={t('settings_searching')} /></div>
      <div><label htmlFor="settings-timezone">{t('settings_label_timezone')}</label><SearchableSelect id="settings-timezone" value={timezone} options={data.timezones} onChange={setTimezone} placeholder={t('settings_searching')} /></div>
    </div>
    <div className="settings-general-footer"><span role="status">{message}</span><button type="submit" disabled={update.isPending}><i aria-hidden="true" className="ph-light ph-check" /> {t('settings_btn_save')}</button></div>
  </form>;
}

function NotificationItem({ notification }: { notification: EventNotificationRow }) {
  const { t } = useLocale();
  const updateNotification = useUpdateNotification();
  const [enabled, setEnabled] = useState(notification.is_enabled === 1);
  const [minutes, setMinutes] = useState(String(notification.reminder_minutes ?? 30));

  useEffect(() => {
    setEnabled(notification.is_enabled === 1);
    setMinutes(String(notification.reminder_minutes ?? 30));
  }, [notification]);

  const save = useCallback(
    (nextEnabled: boolean, nextMinutes: number | null) => {
      updateNotification.mutate({
        eventName: notification.notification_key,
        enabled: nextEnabled,
        reminderMinutes: nextMinutes,
      });
    },
    [notification.notification_key, updateNotification],
  );

  return (
    <div className="settings-notification-item">
      <div className="settings-notification-info">
        {updateNotification.isError || updateNotification.data?.success === false ? <span role="alert">{t('security_request_error')}</span> : null}
        <h4 className="settings-notification-name">{notification.notification_name}</h4>
      </div>
      <div className="settings-notification-controls">
        <label className="settings-toggle">
          <input
            type="checkbox"
            className="notification-toggle"
            aria-label={notification.notification_name}
            disabled={updateNotification.isPending}
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              save(e.target.checked, parseInt(minutes, 10) || null);
            }}
          />
          <span className="settings-toggle-slider" />
        </label>
        <div className="settings-reminder-wrapper">
          <input
            type="number"
            className="settings-reminder-input notification-reminder"
            value={minutes}
            aria-label={t('settings_minutes_before')}
            min={5}
            max={1440}
            step={5}
            placeholder="min"
            onChange={(e) => setMinutes(e.target.value)}
          />
          <span className="settings-reminder-label">{t('settings_minutes_before')}</span>
          <button
            type="button"
            className="settings-reminder-save-btn"
            title={t('settings_btn_save')}
            onClick={() => {
              const m = parseInt(minutes, 10);
              if (m >= 5 && m <= 1440) save(enabled, m);
            }}
          >
            <i aria-hidden="true" className="ph-light ph-check" />
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationsTab() {
  const { t } = useLocale();
  const { data, isLoading } = useSettings();

  if (isLoading || !data) return <div className="settings-content">...</div>;

  return (
    <div className="settings-content">
      <div className="settings-notifications-list">
        {data.eventNotifications.length === 0 ? (
          <div className="settings-empty">{t('settings_no_events')}</div>
        ) : (
          data.eventNotifications.map((n) => <NotificationItem key={n.id} notification={n} />)
        )}
      </div>
    </div>
  );
}

export function SettingsPage() {
  const { t } = useLocale();
  const [activeTab, setActiveTab] = useState<TabId>('general-tab');

  return (
    <div className="container-ingame settings-page">
      <div className="settings-container-full">
        <SimpleMenu title={t('settings')} />

        <div className="settings-main-wrapper">
          <div className="settings-tabs" role="group" aria-label={t('settings')}>
            <button
              type="button"
              className={`settings-tab-btn${activeTab === 'general-tab' ? ' active' : ''}`}
              aria-pressed={activeTab === 'general-tab'}
              onClick={() => setActiveTab('general-tab')}
            >
              <i aria-hidden="true" className="ph-light ph-gear" />
              <span>{t('settings_tab_general')}</span>
            </button>
            <button
              type="button"
              className={`settings-tab-btn${activeTab === 'notifications-tab' ? ' active' : ''}`}
              aria-pressed={activeTab === 'notifications-tab'}
              onClick={() => setActiveTab('notifications-tab')}
            >
              <i aria-hidden="true" className="ph-light ph-bell" />
              <span>{t('settings_tab_notifications')}</span>
            </button>
            <button
              type="button"
              className={`settings-tab-btn${activeTab === 'restrictions-tab' ? ' active' : ''}`}
              aria-pressed={activeTab === 'restrictions-tab'}
              onClick={() => setActiveTab('restrictions-tab')}
            >
              <i aria-hidden="true" className="ph-light ph-shield-check" />
              <span>{t('settings_tab_restrictions')}</span>
            </button>
          </div>

          <div className="settings-tab-content">
            <div
              id="general-tab"
              className={`settings-tab-pane${activeTab === 'general-tab' ? ' active' : ''}`}
              style={{ display: activeTab === 'general-tab' ? 'block' : 'none' }}
            >
              <GeneralTab />
            </div>
            <div
              id="notifications-tab"
              className={`settings-tab-pane${activeTab === 'notifications-tab' ? ' active' : ''}`}
              style={{ display: activeTab === 'notifications-tab' ? 'block' : 'none' }}
            >
              <NotificationsTab />
            </div>
            <div
              id="restrictions-tab"
              className={`settings-tab-pane${activeTab === 'restrictions-tab' ? ' active' : ''}`}
              style={{ display: activeTab === 'restrictions-tab' ? 'block' : 'none' }}
            >
              <SecurityPanel />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
